import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { getUsersCompliance } from "../../../api/getOverviewData";
import { Divider, Grid, Paper, Skeleton, Typography } from "@mui/material";
import { ApexOptions } from "apexcharts";
import { useTheme } from "@mui/material/styles";
import ReactApexChart from "react-apexcharts";
import { t } from "i18next";

type ComplianceProps = {
  projectCodes: string[];
  height: number;
};

export type ComplianceRef = {
  isReady: () => boolean;
  getPercentage: () => number[];
  getCompliantUsers: () => number;
  getReports: () => number;
  getTotalUsers: () => number;
};

const Compliance = forwardRef<ComplianceRef, ComplianceProps>(
  function Compliance({ projectCodes, height }, ref) {
    const [isLoading, setIsLoading] = useState(true);
    const [percentage, setPercentage] = useState<number[]>([]);
    const [compliantUsers, setCompliantUsers] = useState<number>(0);
    const [numReports, setNumReports] = useState<number>(0);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const theme = useTheme();

    useImperativeHandle(ref, () => ({
      isReady: () => isLoading,
      getPercentage: () => percentage,
      getCompliantUsers: () => compliantUsers,
      getReports: () => numReports,
      getTotalUsers: () => totalUsers,
    }));

    useEffect(() => {
      setIsLoading(true);

      if (projectCodes.length === 0) return;
      let controller = new AbortController();
      let isMounted = true;
      const fetchUsersCompliance = async () => {
        try {
          const data = await getUsersCompliance(projectCodes, controller);
          if (isMounted) {
            setPercentage([data.percentage]);
            setCompliantUsers(data.compliantUsers);
            setNumReports(data.numberReports);
            setTotalUsers(data.totalUsers);
          }
        } catch (error: any) {
          if (error.name === "AbortError") {
            console.log("Fetch aborted");
          } else {
            console.error("Fetch error:", error);
          }
        } finally {
          if (isMounted) setIsLoading(false);
        }
      };

      fetchUsersCompliance();

      return () => {
        isMounted = false;
        controller.abort(); // Cancel the request when projectCodes changes
      };
    }, [projectCodes]);

    const [options, setOptions] = useState<ApexOptions>({
      chart: {
        height: 350,
        type: "radialBar",
      },
      plotOptions: {
        radialBar: {
          hollow: {
            margin: 0,
            size: "55%",
          },
          dataLabels: {
            name: {
              offsetY: -10,
              fontSize: "20px",
              fontFamily: "Roboto",
              fontWeight: "Normal",
              color: theme.palette.text.secondary,
            },
            value: {
              formatter: function (val) {
                return Math.round(val).toString() + "%";
              },
              fontSize: "36px",
              color: theme.palette.text.primary,
              show: true,
            },
          },
          track: {
            dropShadow: {
              enabled: true,
              top: 15,
              left: 5,
              blur: 12,
              opacity: 0.2,
            },
            //background: "#4bc0c01a",
          },
        },
      },
      fill: {
        type: "gradient",
        gradient: {
          shade: "dark",
          type: "vertical",
          colorStops: [
            {
              offset: 0,
              color: "rgb(99, 212, 212)",
              opacity: 1,
            },
            {
              offset: 100,
              color: "rgba(75, 192, 192, 1)",
              opacity: 1,
            },
          ],
        },
      },
      stroke: {
        lineCap: "round",
      },
      labels: ["Compliance"],
    });

    return percentage.length > 0 && isLoading === false ? (
      <Paper sx={{ borderRadius: "5px", height: { height } }}>
        <Grid
          container
          flexDirection={"row"}
          height={"100%"}
          justifyContent={"space-between"}
          p={2}
        >
          <Grid item xs>
            <ReactApexChart
              options={options}
              series={percentage}
              type="radialBar"
              height={"100%"}
            />
          </Grid>

          <Grid item xs>
            <Grid container height="100%" flexDirection={"column"}>
              <Grid item xs alignContent={"center"}>
                <Typography
                  variant="h4"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {compliantUsers}/{totalUsers}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: "normal",
                  }}
                >
                  {`${t("users-have-gathered-enough-data")}`}
                </Typography>
              </Grid>
              <Grid item xs alignContent={"center"}>
                <Typography
                  variant="h4"
                  sx={{ color: theme.palette.text.primary }}
                >
                  {numReports}/{totalUsers}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontWeight: "normal",
                  }}
                >
                  {`${t("users-have-received-feedback-reports")}`}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>
    ) : (
      <Skeleton
        sx={{
          borderRadius: "5px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.2)",
        }}
        animation="pulse"
        variant="rectangular"
        width={"100%"}
        height={height}
      />
    );
  }
);

export { Compliance };
